import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button, Spinner } from "@safers/ui";
import { Modal, ModalContent, ModalHeader, ModalBody, ModalTitle } from "@safers/ui/organisms";
import { useAuthStore, selectUser, canAccessSite, canAccessMenuSite } from "@safers/auth";
import { eventService, type Event, type GasPayload, type HeatIndexPayload } from "@safers/api";
import { useLandscapeVideo } from "@/hooks/useIsFullscreen";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cctvLivePath, controlPath } from "@/routes/paths";
import { EmptyState } from "@/components/EmptyState";
import { EmptyIcon } from "@/components/emptyIcons";
import {
  GAS_FIELDS,
  HEAT_LEVEL_META,
  heatLevelFromSeverity,
  heatLevelOfPoint,
  isCctvEventCategory,
  severityChip,
  type HeatLevel,
} from "@/services";
import { SEVERITY_LABELS } from "@/lib/map";
import { typeLabel, categoryLabel } from "@/lib/eventLabels";

const eventKeys = {
  detail: (id: number) => ["event", id] as const,
};

interface Props {
  eventId: number | null;
  onClose: () => void;
}

interface EventProps {
  event: Event;
}

interface InfoRowProps {
  label: string;
  value: string;
}

interface MeasureBar {
  key: string;
  label: string;
  value: string;
  /** null 이면 판정할 수 없다(값 없음·alarmFields 없음) — 막대를 그리지 않는다 */
  level: HeatLevel | null;
  /** 서버가 이벤트 원인으로 짚은 가스(`alarmFields`). 등급을 모를 때 등급 자리에 "원인" 으로 보인다 */
  cause?: boolean;
}

/** 등급이 없는 보조 값(체감온도의 온도·습도) */
interface MeasureNote {
  label: string;
  value: string;
}

interface Measure {
  bars: MeasureBar[];
  notes: MeasureNote[];
}

interface LevelBarsProps {
  bars: MeasureBar[];
}

function formatDateTime(timestamp: string): string {
  const d = new Date(timestamp);
  if (!Number.isFinite(d.getTime())) return "—";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function InfoRow({ label, value }: InfoRowProps) {
  return (
    <div className="flex items-baseline gap-3 py-1.5">
      <span className="w-16 flex-none text-[0.8125rem] text-neutral-500">{label}</span>
      <span className="min-w-0 flex-1 truncate text-[0.8125rem] font-semibold text-neutral-900">
        {value}
      </span>
    </div>
  );
}

/* ── 관제(영상 감지 등) ───────────────────────────────────────────── */

function Media({ event }: EventProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  useLandscapeVideo(boxRef, isMobile);

  if (event.video?.url) {
    return (
      <div ref={boxRef} className="overflow-hidden rounded-lg bg-black">
        <video
          src={event.video.url}
          controls
          autoPlay
          muted
          playsInline
          className="max-h-[20rem] w-full object-contain sm:max-h-[50vh]"
        />
      </div>
    );
  }
  if (event.snapshot?.url) {
    return (
      <div className="overflow-hidden rounded-lg bg-neutral-100">
        <img
          src={event.snapshot.url}
          alt={`${event.name} 스냅샷`}
          className="max-h-[20rem] w-full object-contain sm:max-h-[50vh]"
        />
      </div>
    );
  }
  return (
    <div className="flex h-[8rem] items-center justify-center rounded-lg bg-neutral-50 text-[0.8125rem] text-neutral-500">
      미디어 없음
    </div>
  );
}

function DetectionDetail({ event }: EventProps) {
  const navigate = useNavigate();
  const user = useAuthStore(selectUser);

  const siteId = event.site?.id ?? null;
  const canSite = siteId != null && canAccessSite(user, siteId);
  const canCctv =
    siteId != null &&
    isCctvEventCategory(event.category) &&
    canAccessMenuSite(user, siteId, "CCTV");

  const camStream = canCctv && event.path ? event.path : null;
  const cctvHref = siteId == null ? null : cctvLivePath({ siteId, cctvId: camStream });

  // replace — 이동하면 `?event=` 가 빠져 모달이 닫히고, 뒤로가기가 모달로 돌아오지 않는다
  const go = (path: string) => navigate(path, { replace: true });

  return (
    <div className="flex flex-col gap-3">
      <Media event={event} />
      <div className="divide-y divide-neutral-100">
        <InfoRow label="현장" value={event.site?.name ?? "—"} />
        <InfoRow label="유형" value={typeLabel(event.type)} />
        <InfoRow label="구분" value={categoryLabel(event.category)} />
        <InfoRow label="발생" value={formatDateTime(event.timestamp)} />
        {Number.isFinite(event.confidence) && event.confidence > 0 && (
          <InfoRow label="신뢰도" value={`${Math.round(event.confidence * 100)}%`} />
        )}
      </div>

      <div className="flex flex-wrap gap-2 pt-1">
        <Button
          size="sm"
          variant="outline"
          disabled={!canSite}
          title={canSite ? undefined : "이 현장의 접근 권한이 없습니다"}
          onClick={() => siteId != null && go(controlPath(siteId))}
        >
          현장으로 이동
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={!canCctv}
          title={canCctv ? undefined : "이 현장의 CCTV 권한이 없거나 영상 이벤트가 아닙니다"}
          onClick={() => cctvHref && go(cctvHref)}
        >
          CCTV에서 보기
        </Button>
      </div>
    </div>
  );
}

/* ── 보건 ─────────────────────────────────────────────────────────── */

/** 아래(정상)부터 위(위험)로. 막대는 자기 등급 칸까지 올라간다. */
const LEVELS: HeatLevel[] = ["SAFE", "INTEREST", "CAUTION", "WARNING", "DANGER"];

/**
 * 한 칸 높이(rem). 칸·y 축 글자·막대가 모두 이 값의 배수라 막대 끝이 칸 경계에 딱 맞는다 —
 * 막대를 % 로 잡으면 틀의 테두리만큼 어긋난다.
 */
const ROW_REM = 2;

/** 값이 없으면 `—`. 소수점은 있을 때만 한 자리 */
function formatValue(value: number | null, unit: string): string {
  if (value == null) return "—";
  return `${value.toLocaleString("ko-KR", { maximumFractionDigits: 1 })} ${unit}`;
}

function asGas(payload: Event["payload"]): GasPayload | null {
  return payload?.kind === "gas" ? (payload as unknown as GasPayload) : null;
}

function asHeatIndex(payload: Event["payload"]): HeatIndexPayload | null {
  return payload?.kind === "heat-index" ? (payload as unknown as HeatIndexPayload) : null;
}

/**
 * 가스는 서버가 원인으로 짚은 항목(`alarmFields`)에 이벤트 등급을, 나머지에 정상을 붙인다.
 * 항목별 등급과 단계별 기준값은 아직 받지 못해, 측정값으로 판정하지 않는다.
 * 원인 항목인데 이벤트 등급이 없으면(null 등) 정상이라 할 수 없어 등급을 비우고 "원인" 만 보인다.
 */
function gasBars(payload: GasPayload, event: Event): MeasureBar[] {
  return GAS_FIELDS.map((f) => {
    const value = payload[f.key];
    const cause = payload.alarmFields?.includes(f.key) ?? false;
    let level: HeatLevel | null = null;
    if (value != null && payload.alarmFields != null) {
      const severityLevel = heatLevelFromSeverity(event.severity);
      level = !cause ? "SAFE" : severityLevel === "SAFE" ? null : severityLevel;
    }
    return { key: f.key, label: f.label, value: formatValue(value, f.unit), level, cause };
  });
}

/**
 * 등급이 있는 체감온도만 막대로 그린다. 온도·습도는 체감온도 계산에 쓰이는 값일 뿐 등급이
 * 없어서 아래에 숫자로만 둔다.
 */
function heatIndexMeasure(payload: HeatIndexPayload, event: Event): Measure {
  const value = payload.apparentTemperature;
  return {
    bars: [
      {
        key: "apparentTemperature",
        label: "체감온도",
        value: formatValue(value, "℃"),
        level:
          value == null ? null : heatLevelOfPoint({ heatIndex: value, severity: event.severity }),
      },
    ],
    notes: [
      { label: "온도", value: formatValue(payload.temperature, "℃") },
      { label: "습도", value: formatValue(payload.humidity, "%") },
    ],
  };
}

function measureOf(event: Event): Measure | null {
  const gas = asGas(event.payload);
  if (gas) return { bars: gasBars(gas, event), notes: [] };
  const heat = asHeatIndex(event.payload);
  if (heat) return heatIndexMeasure(heat, event);
  return null;
}

/**
 * 등급 막대. y 축은 숫자가 아니라 등급 5칸이다 — 기준값이 없어 "대략 어느 등급까지 갔나"만
 * 보여준다. 실제 값은 막대 아래 숫자로 읽는다.
 */
function LevelBars({ bars }: LevelBarsProps) {
  return (
    <div className="flex gap-2">
      {/* y 축 — 위가 위험. 높이를 그래프와 같게 묶는다 — 늘어나면 아래 항목 줄까지 내려간다 */}
      <div
        className="flex flex-none flex-col-reverse self-start"
        style={{ height: `${LEVELS.length * ROW_REM}rem` }}
      >
        {LEVELS.map((lv) => (
          <span
            key={lv}
            className="flex items-center justify-end text-[0.75rem] text-neutral-500"
            style={{ height: `${ROW_REM}rem` }}
          >
            {HEAT_LEVEL_META[lv].label}
          </span>
        ))}
      </div>

      <div className="min-w-0 flex-1">
        <div className="relative" style={{ height: `${LEVELS.length * ROW_REM}rem` }}>
          {/* 등급 구간 — 옅게 */}
          <div className="absolute inset-0 flex flex-col-reverse">
            {LEVELS.map((lv) => (
              <div
                key={lv}
                className="border-t border-dashed border-neutral-200"
                style={{ height: `${ROW_REM}rem`, backgroundColor: HEAT_LEVEL_META[lv].panelBg }}
              />
            ))}
          </div>

          <div
            className="relative grid h-full"
            style={{ gridTemplateColumns: `repeat(${bars.length}, minmax(0, 1fr))` }}
          >
            {bars.map((b) => {
              const step = b.level ? LEVELS.indexOf(b.level) : -1;
              const meta = b.level ? HEAT_LEVEL_META[b.level] : null;
              return (
                <div key={b.key} className="flex items-end justify-center">
                  {meta && step >= 0 && (
                    <div
                      title={`${b.label} ${b.value} · ${meta.label}`}
                      className="w-full max-w-10 rounded-t"
                      style={{
                        height: `${(step + 1) * ROW_REM}rem`,
                        backgroundColor: meta.fill,
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* x 축 — 항목, 측정값, 등급 */}
        <div
          className="grid border-t border-neutral-300 pt-1.5"
          style={{ gridTemplateColumns: `repeat(${bars.length}, minmax(0, 1fr))` }}
        >
          {bars.map((b) => (
            <div key={b.key} className="flex flex-col items-center text-center">
              <span className="text-[0.75rem] font-bold text-neutral-800">{b.label}</span>
              <span className="text-[0.75rem] tabular-nums text-neutral-600">{b.value}</span>
              <span className="text-[0.6875rem] text-neutral-500">
                {b.level ? HEAT_LEVEL_META[b.level].label : b.cause ? "원인" : "—"}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function HealthDetail({ event }: EventProps) {
  const measure = measureOf(event);

  return (
    <div className="flex flex-col gap-4">
      <div className="divide-y divide-neutral-100">
        <InfoRow label="현장" value={event.site?.name ?? "—"} />
        <InfoRow label="유형" value={typeLabel(event.type)} />
        <InfoRow label="장비" value={event.deviceId ?? "—"} />
        <InfoRow label="발생" value={formatDateTime(event.timestamp)} />
      </div>

      <section>
        <h3 className="mb-2 text-[0.8125rem] font-bold text-neutral-700">측정값</h3>
        {measure ? (
          <div className="rounded-lg border border-neutral-200 p-3">
            <LevelBars bars={measure.bars} />
            {measure.notes.length > 0 && (
              <div className="mt-3 flex justify-center gap-4 border-t border-neutral-100 pt-2 text-[0.75rem]">
                {measure.notes.map((n) => (
                  <span key={n.label} className="text-neutral-500">
                    {n.label}{" "}
                    <span className="font-semibold tabular-nums text-neutral-800">{n.value}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex min-h-[8rem] rounded-lg border border-neutral-200">
            <EmptyState icon={EmptyIcon.device} title="측정값 없음" />
          </div>
        )}
      </section>
    </div>
  );
}

/* ── 틀 ───────────────────────────────────────────────────────────── */

/**
 * 이벤트 상세. 알림 벨·배너·푸시·관제·보건 어디서 열든 이 모달 하나다(`MobileShell` 에 전역 마운트).
 *
 * 틀(헤더·로딩·오류)은 같고 본문만 **이벤트의 category** 로 고른다 — 연 화면이 아니라
 * 이벤트가 정한다. 보건은 측정값 표, 그 외(영상 감지·안전 센서)는 영상과 CCTV 이동.
 */
export function EventDetailModal({ eventId, onClose }: Props) {
  const { data, isLoading, isError } = useQuery({
    queryKey: eventKeys.detail(eventId ?? -1),
    queryFn: () => eventService.get(eventId as number),
    enabled: eventId != null,
    staleTime: Infinity,
  });
  const isHealth = data?.category === "HEALTH";

  return (
    <Modal open={eventId != null} onOpenChange={(open) => !open && onClose()}>
      <ModalContent
        aria-describedby={undefined}
        className={`max-h-[90dvh] w-[calc(100%-2rem)] overflow-y-auto ${isHealth ? "" : "sm:max-w-[48rem]"}`}
      >
        <ModalHeader className="p-4 pb-3 pr-14 sm:p-6 sm:pb-3 sm:pr-14">
          <ModalTitle className="flex min-w-0 items-center gap-2 text-base sm:text-lg">
            {data?.severity && (
              <span
                className={`flex-none rounded px-1.5 py-0.5 text-[0.75rem] font-bold ${severityChip(data.severity).bg} ${severityChip(data.severity).text}`}
              >
                {SEVERITY_LABELS[data.severity]}
              </span>
            )}
            <span className="truncate">{data?.name ?? "이벤트 상세"}</span>
          </ModalTitle>
        </ModalHeader>

        <ModalBody className="px-4 pb-5 sm:px-6 sm:pb-6">
          {isLoading && (
            <div className="flex h-[12rem] items-center justify-center">
              <Spinner size="lg" />
            </div>
          )}

          {isError && (
            <EmptyState
              icon={EmptyIcon.event}
              title="이벤트를 불러오지 못했습니다"
              hint="삭제되었거나 접근 권한이 없을 수 있습니다."
            />
          )}

          {data && (isHealth ? <HealthDetail event={data} /> : <DetectionDetail event={data} />)}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
